import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import i18n from '@/lib/i18n.js';
import { useTenantStore } from '@/store/tenant.store.js';
import {
    readReportStudioNavigationIntent,
    withoutReportStudioIntent,
} from '@/lib/report-studio.js';
import { ReportStudioHandoffContext } from '@/contexts/report-studio-handoff.context.js';

function handoffUnavailableMessage() {
    return i18n.t('reportSurface:handoff.unavailable', {
        defaultValue: 'The requested report is not available on this source page.',
    });
}

export function ReportStudioHandoffProvider({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const tenantId = useTenantStore((state) => state.selectedTenantId);
    const [intent, setIntent] = useState(null);
    const claimedBy = useRef('');
    const surfaces = useRef(new Map());
    const previousTenantId = useRef(tenantId);
    const intentPath = useRef('');

    useEffect(() => {
        const parsed = readReportStudioNavigationIntent(location.state);
        if (!parsed) return;
        claimedBy.current = '';
        surfaces.current.clear();
        intentPath.current = location.pathname;
        setIntent(parsed);
        navigate(`${location.pathname}${location.search}${location.hash}`, {
            replace: true,
            state: withoutReportStudioIntent(location.state),
        });
    }, [location.hash, location.pathname, location.search, location.state, navigate]);

    useEffect(() => {
        if (!intent || !intentPath.current || intentPath.current === location.pathname) return undefined;
        claimedBy.current = '';
        intentPath.current = '';
        setIntent(null);
        return undefined;
    }, [intent, location.pathname]);

    useEffect(() => {
        if (!intent) return undefined;
        void i18n.loadNamespaces('reportSurface');
        const timer = window.setTimeout(() => {
            claimedBy.current = '';
            intentPath.current = '';
            setIntent(null);
            toast.error(handoffUnavailableMessage());
        }, 30_000);
        return () => window.clearTimeout(timer);
    }, [intent]);

    useEffect(() => {
        if (previousTenantId.current === tenantId) return;
        previousTenantId.current = tenantId;
        claimedBy.current = '';
        intentPath.current = '';
        setIntent(null);
    }, [tenantId]);

    const register = useCallback((instanceId, state) => {
        surfaces.current.set(instanceId, state);
        return () => {
            surfaces.current.delete(instanceId);
            if (claimedBy.current === instanceId) claimedBy.current = '';
        };
    }, []);

    const claim = useCallback((instanceId, reportKeys) => {
        if (!intent || (claimedBy.current && claimedBy.current !== instanceId)) return null;
        if (!reportKeys.includes(intent.reportKey)) return null;
        claimedBy.current = instanceId;
        return intent;
    }, [intent]);

    const consume = useCallback((instanceId) => {
        if (claimedBy.current && claimedBy.current !== instanceId) return;
        claimedBy.current = '';
        intentPath.current = '';
        setIntent(null);
    }, []);

    const reject = useCallback((instanceId) => {
        if (claimedBy.current && claimedBy.current !== instanceId) return;
        claimedBy.current = '';
        intentPath.current = '';
        setIntent(null);
        toast.error(handoffUnavailableMessage());
    }, []);

    const value = useMemo(
        () => ({ intent, register, claim, consume, reject }),
        [claim, consume, intent, register, reject],
    );
    return <ReportStudioHandoffContext.Provider value={value}>{children}</ReportStudioHandoffContext.Provider>;
}
